import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Header from '../../components/feature/Header'
import Footer from '../home/components/Footer'
import { Input } from '../../components/base/Input'
import { Button } from '../../components/base/Button'
import { Modal } from '../../components/base/Modal'
import AvatarSelector from '../../components/feature/AvatarSelector'
import AvatarDisplay from '../../components/feature/AvatarDisplay'
import ReputationLevelBadge from '../../components/base/ReputationLevelBadge'
import UserBadge from '../../components/base/UserBadge'
import VerifiedCreatorBadge from '../../components/base/VerifiedCreatorBadge'
import BadgeShowcase from './components/BadgeShowcase'
import ProfileActivityHistory from './components/ProfileActivityHistory'
import MyGroupsList from './components/MyGroupsList'
import MyAIProjectsTab from './components/MyAIProjectsTab'
import MyWatchlist from './components/MyWatchlist'
import CreatorAnalyticsDashboard from './components/CreatorAnalyticsDashboard'
import ApplicationStatusTracker from './components/ApplicationStatusTracker'
import AIDramaApplyModal from '../../components/base/AIDramaApplyModal'
import { useAuth } from '../../hooks/useAuth'
import { useReputation } from '../../hooks/useReputation'
import { useProfileStats } from '../../hooks/useProfileStats'
import { supabase } from '../../lib/supabase'

interface UserStats {
  posts: number
  comments: number
  followers: number
  following: number
  reputation: number
  groups: number
}

export const ProfilePage = () => {
  const { user, profile, updateProfile, createProfile, loading } = useAuth()
  const { unlockedBadges, lockedBadges, level, nextLevel, progress, totalBadges, featuredBadge, stats: repStats } = useReputation(user?.id)
  const { stats, loading: statsLoading } = useProfileStats(user?.id)
  const [username, setUsername] = useState('')
  const [bio, setBio] = useState('')
  const [selectedAvatar, setSelectedAvatar] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showAvatarSelector, setShowAvatarSelector] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'badges' | 'projects' | 'watchlist' | 'analytics' | 'groups' | 'activity' | 'settings'>('overview')
  const [showAIDramaApply, setShowAIDramaApply] = useState(false)
  const [hasApplication, setHasApplication] = useState<boolean | null>(null)

  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '')
      setBio(profile.bio || '')
      setSelectedAvatar(profile.avatar_id || '')
    } else if (user && !loading) {
      setIsEditing(true)
    }
  }, [profile, user, loading])

  useEffect(() => {
    if (user?.id) {
      checkApplication()
    }
  }, [user?.id])

  const checkApplication = async () => {
    if (!user?.id) return
    try {
      const { data } = await supabase
        .from('creator_applications')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle()
      setHasApplication(!!data)
    } catch {
      setHasApplication(false)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!username.trim()) {
      setError('Username is required')
      return
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters')
      return
    }

    if (username.length > 30) {
      setError('Username must be less than 30 characters')
      return
    }

    const usernameRegex = /^[a-zA-Z0-9_-]+$/
    if (!usernameRegex.test(username.trim())) {
      setError('Username can only contain letters, numbers, underscores, and hyphens')
      return
    }

    setSaveLoading(true)
    setError('')
    setSuccess('')

    try {
      if (!profile) {
        const result = await createProfile(username.trim())
        if (result.error) {
          setError(result.error.message || 'Failed to create profile')
          setSaveLoading(false)
          return
        }
        
        const updateData: any = {}
        if (bio.trim()) updateData.bio = bio.trim()
        if (selectedAvatar) updateData.avatar_id = selectedAvatar
        
        if (Object.keys(updateData).length > 0) {
          const updateResult = await updateProfile(updateData)
          if (updateResult.error) {
            setError(updateResult.error.message || 'Profile created but failed to update additional info')
            setSaveLoading(false)
            return
          }
        }
      } else {
        const updateData: any = { username: username.trim() }
        if (bio.trim() !== (profile.bio || '')) updateData.bio = bio.trim() || null
        if (selectedAvatar !== (profile.avatar_id || '')) updateData.avatar_id = selectedAvatar || null
        
        const result = await updateProfile(updateData)
        
        if (result.error) {
          if (result.error.message.includes('duplicate') || result.error.message.includes('unique')) {
            setError('This username is already taken')
          } else {
            setError(result.error.message || 'Failed to update profile')
          }
          setSaveLoading(false)
          return
        }
      }

      setSuccess('Profile updated successfully!')
      setIsEditing(false)
      setTimeout(() => setSuccess(''), 3000)
      
    } catch (err: any) {
      console.error('Profile update error:', err)
      setError(err.message || 'An unexpected error occurred')
    } finally {
      setSaveLoading(false)
    }
  }

  const handleCancel = () => {
    if (profile) {
      setUsername(profile.username || '')
      setBio(profile.bio || '')
      setSelectedAvatar(profile.avatar_id || '')
      setIsEditing(false)
    }
    setError('')
    setSuccess('')
  }

  const handleAvatarSelect = (avatarId: string) => {
    setSelectedAvatar(avatarId)
    setShowAvatarSelector(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
                <div className="space-y-3 flex-1">
                  <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-user-line text-3xl text-gray-400"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign In Required</h1>
            <p className="text-gray-600 mb-6">Please sign in to view and manage your profile.</p>
            <Link to="/" className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Profile Header Card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          {/* Cover Image */}
          <div className="h-32 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 relative">
            <div className="absolute inset-0 bg-black/10"></div>
          </div>
          
          {/* Profile Info */}
          <div className="px-8 pb-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between -mt-12 relative z-10">
              <div className="flex items-end space-x-5">
                <div className="relative">
                  <div className="w-28 h-28 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-white">
                    <AvatarDisplay
                      avatarId={selectedAvatar}
                      username={profile?.username}
                      email={user?.email}
                      size="xl"
                      className="w-full h-full"
                    />
                  </div>
                  {profile?.is_verified_creator && (
                    <div className="absolute -bottom-1 -right-1 z-10">
                      <VerifiedCreatorBadge size="md" />
                    </div>
                  )}
                  {isEditing && (
                    <button
                      onClick={() => setShowAvatarSelector(true)}
                      className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white hover:bg-amber-600 transition-colors cursor-pointer shadow-md"
                    >
                      <i className="ri-camera-line text-sm"></i>
                    </button>
                  )}
                </div>
                
                <div className="pb-2">
                  <div className="flex items-center space-x-2 mb-1">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {profile?.username || 'Set up your profile'}
                    </h1>
                    {profile?.is_verified_creator && (
                      <VerifiedCreatorBadge size="sm" showLabel />
                    )}
                    <ReputationLevelBadge points={stats.reputation} size="sm" />
                  </div>
                  <p className="text-gray-500 text-sm">{user?.email}</p>
                  {/* Featured badges row */}
                  {unlockedBadges.length > 0 && !isEditing && (
                    <div className="flex items-center space-x-1.5 mt-2">
                      {unlockedBadges.slice(0, 5).map((badge) => (
                        <UserBadge key={badge.id} badge={badge} size="sm" />
                      ))}
                      {unlockedBadges.length > 5 && (
                        <button
                          onClick={() => setActiveTab('badges')}
                          className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-xs text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
                        >
                          +{unlockedBadges.length - 5}
                        </button>
                      )}
                    </div>
                  )}
                  {profile?.bio && !isEditing && (
                    <p className="text-gray-600 mt-2 max-w-md text-sm">{profile.bio}</p>
                  )}
                </div>
              </div>
              
              <div className="mt-4 md:mt-0">
                {!isEditing && profile && (
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          {[
            { label: 'Posts', value: stats.posts, icon: 'ri-article-line', color: 'from-amber-400 to-orange-400' },
            { label: 'Comments', value: stats.comments, icon: 'ri-chat-3-line', color: 'from-emerald-400 to-teal-400' },
            { label: 'Followers', value: stats.followers, icon: 'ri-user-heart-line', color: 'from-pink-400 to-rose-400' },
            { label: 'Following', value: stats.following, icon: 'ri-user-follow-line', color: 'from-cyan-400 to-sky-400' },
            { label: 'Groups', value: stats.groups, icon: 'ri-group-2-line', color: 'from-teal-400 to-emerald-400', tab: 'groups' },
            { label: 'Badges', value: unlockedBadges.length, icon: 'ri-trophy-line', color: 'from-amber-400 to-yellow-400', tab: 'badges' }
          ].map((stat) => (
            <div 
              key={stat.label} 
              className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" 
              onClick={() => stat.tab ? setActiveTab(stat.tab as any) : undefined}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <i className={`${stat.icon} text-white text-lg`}></i>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums transition-all duration-300">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-100">
            <div className="flex space-x-1 p-1 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: 'ri-user-line' },
                { id: 'badges', label: 'Badges', icon: 'ri-trophy-line' },
                { id: 'projects', label: 'My AI Projects', icon: 'ri-movie-2-line' },
                { id: 'watchlist', label: 'My Watchlist', icon: 'ri-bookmark-3-line' },
                { id: 'analytics', label: 'Analytics', icon: 'ri-line-chart-line' },
                { id: 'groups', label: 'My Groups', icon: 'ri-group-2-line' },
                { id: 'activity', label: 'Activity', icon: 'ri-history-line' },
                { id: 'settings', label: 'Settings', icon: 'ri-settings-3-line' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-amber-50 text-amber-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
                <i className="ri-error-warning-line text-red-500 text-xl"></i>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
                <i className="ri-check-line text-emerald-500 text-xl"></i>
                <p className="text-emerald-700 text-sm">{success}</p>
              </div>
            )}

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {isEditing ? (
                  <form onSubmit={handleSave} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Username <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <i className="ri-at-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Choose a unique username"
                            disabled={saveLoading}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          3-30 characters, letters, numbers, underscores, hyphens
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <div className="relative">
                          <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                          <input
                            type="email"
                            value={user?.email || ''}
                            disabled
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Email cannot be changed
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bio
                      </label>
                      <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        placeholder="Tell us about yourself and your favorite dramas..."
                        rows={4}
                        maxLength={500}
                        disabled={saveLoading}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none text-sm"
                      />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500">
                          Share your interests with the community
                        </p>
                        <p className="text-xs text-gray-500">
                          {bio.length}/500
                        </p>
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={saveLoading}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      >
                        {saveLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="ri-check-line mr-2"></i>
                            Save Changes
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={saveLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Username</p>
                        <p className="text-gray-900 font-medium">{profile?.username || 'Not set'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Email</p>
                        <p className="text-gray-900 font-medium">{user?.email}</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Bio</p>
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {profile?.bio || 'No bio added yet. Click "Edit Profile" to add one!'}
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Member Since</p>
                        <p className="text-gray-900 font-medium">
                          {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }) : 'N/A'}
                        </p>
                      </div>
                      {profile && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reputation Level</p>
                          <div className="flex items-center space-x-2">
                            <i className={`${level.icon} text-lg ${level.color}`}></i>
                            <p className="text-gray-900 font-medium">Level {level.level}: {level.name}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* AI Drama Creator Application CTA or Status Tracker */}
                    {hasApplication && user ? (
                      <ApplicationStatusTracker
                        userId={user.id}
                        onApplyAgain={() => setShowAIDramaApply(true)}
                      />
                    ) : (
                      <div className="bg-gradient-to-r from-orange-500 via-rose-500 to-amber-500 rounded-xl p-5 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                              <i className="ri-robot-2-line text-2xl"></i>
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Become an AI Drama Creator</h4>
                              <p className="text-white/80 text-sm">Create stunning AI-powered dramas and join our creator community</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setShowAIDramaApply(true)}
                            className="px-5 py-2.5 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
                          >
                            <i className="ri-send-plane-line mr-2"></i>
                            Apply Now
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Badges Tab */}
            {activeTab === 'badges' && (
              <BadgeShowcase
                unlockedBadges={unlockedBadges}
                lockedBadges={lockedBadges}
                level={level}
                nextLevel={nextLevel}
                progress={progress}
                reputation={stats.reputation}
                totalBadges={totalBadges}
              />
            )}

            {/* My AI Projects Tab */}
            {activeTab === 'projects' && (
              <MyAIProjectsTab />
            )}

            {/* Watchlist Tab */}
            {activeTab === 'watchlist' && (
              <MyWatchlist />
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <CreatorAnalyticsDashboard />
            )}

            {/* Groups Tab */}
            {activeTab === 'groups' && user && (
              <MyGroupsList userId={user.id} />
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && user && (
              <ProfileActivityHistory userId={user.id} />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <i className="ri-notification-3-line text-amber-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Notifications</h4>
                        <p className="text-sm text-gray-500">Manage your notification preferences</p>
                      </div>
                    </div>
                    <Link to="/settings" className="text-amber-600 hover:text-amber-700 text-sm font-medium cursor-pointer">
                      Configure
                      <i className="ri-arrow-right-s-line ml-1"></i>
                    </Link>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <i className="ri-shield-check-line text-emerald-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Privacy</h4>
                        <p className="text-sm text-gray-500">Control your privacy settings</p>
                      </div>
                    </div>
                    <Link to="/settings" className="text-amber-600 hover:text-amber-700 text-sm font-medium cursor-pointer">
                      Configure
                      <i className="ri-arrow-right-s-line ml-1"></i>
                    </Link>
                  </div>
                </div>

                <div className="border border-red-200 rounded-xl p-5 bg-red-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <i className="ri-delete-bin-line text-red-600"></i>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Delete Account</h4>
                        <p className="text-sm text-gray-500">Permanently delete your account and data</p>
                      </div>
                    </div>
                    <button className="text-red-600 hover:text-red-700 text-sm font-medium cursor-pointer">
                      Delete
                      <i className="ri-arrow-right-s-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Avatar Selector Modal */}
      <Modal isOpen={showAvatarSelector} onClose={() => setShowAvatarSelector(false)} size="xl">
        <AvatarSelector
          selectedAvatar={selectedAvatar}
          onAvatarSelect={handleAvatarSelect}
          onClose={() => setShowAvatarSelector(false)}
        />
      </Modal>

      {/* AI Drama Apply Modal */}
      <AIDramaApplyModal
        isOpen={showAIDramaApply}
        onClose={() => setShowAIDramaApply(false)}
      />

      <Footer />
    </div>
  )
}

export default ProfilePage
